import React, { useState, useEffect, useMemo } from 'react';
import { Dialog } from 'primereact/dialog';
import { InputNumber } from 'primereact/inputnumber';
import { Calendar } from 'primereact/calendar';
import { Button } from 'primereact/button';
import { Dropdown } from 'primereact/dropdown';
import { useLanguage } from '../../../../../../contexts/LanguageContext';
import { classNames } from 'primereact/utils';
import PaymentModelMultiSelect from './PaymentModelMultiSelect';

const BASE_CURRENCY_OPTIONS = [
    { label: 'CAD', value: 'CAD' },
    { label: 'CHF', value: 'CHF' },
    { label: 'DKK', value: 'DKK' },
    { label: 'EUR', value: 'EUR' },
    { label: 'GBP', value: 'GBP' },
    { label: 'JPY', value: 'JPY' },
    { label: 'NOK', value: 'NOK' },
    { label: 'SAR', value: 'SAR' },
    { label: 'SEK', value: 'SEK' },
    { label: 'USD', value: 'USD' },
    { label: 'TRY', value: 'TRY' }
];

const toModelOption = (m) => ({ id: m.id, name: m.name, displayValue: m.name });

const PaymentDialog = ({ visible, data, onHide, onSave, brand, defaultCurrency = null }) => {
    const { th } = useLanguage();
    const [formData, setFormData] = useState({
        id: null,
        siteUseId: 0,
        modelIds: [],
        selectedModels: [],
        warrHourlyRate: null,
        normalHourlyRate: null,
        currencyUnits: '',
        contractStartDate: null,
        contractEndDate: null
    });

    const [submitted, setSubmitted] = useState(false);
    const [dateError, setDateError] = useState(false);

    const currencyOptions = useMemo(() => {
        if (defaultCurrency && !BASE_CURRENCY_OPTIONS.some((o) => o.value === defaultCurrency)) {
            return [{ label: defaultCurrency, value: defaultCurrency }, ...BASE_CURRENCY_OPTIONS];
        }
        return BASE_CURRENCY_OPTIONS;
    }, [defaultCurrency]);

    useEffect(() => {
        setSubmitted(false);
        setDateError(false);

        if (data) {
            const modelIds = data.modelIds || [];
            const selectedModels = Array.isArray(data.models) && data.models.length ? data.models.map(toModelOption) : data.selectedModels || modelIds.map((id) => ({ id, name: String(id), displayValue: String(id) }));

            setFormData({
                id: data.id,
                siteUseId: data.siteUseId || 0,
                modelIds: modelIds,
                selectedModels: selectedModels,
                warrHourlyRate: data.warrHourlyRate ?? null,
                normalHourlyRate: data.normalHourlyRate ?? null,
                currencyUnits: data.currencyUnits || defaultCurrency || '',
                contractStartDate: data.contractStartDate ? new Date(data.contractStartDate) : null,
                contractEndDate: data.contractEndDate ? new Date(data.contractEndDate) : null
            });
        } else {
            setFormData({
                id: null,
                siteUseId: 0,
                modelIds: [],
                selectedModels: [],
                warrHourlyRate: null,
                normalHourlyRate: null,
                currencyUnits: defaultCurrency || '',
                contractStartDate: null,
                contractEndDate: null
            });
        }
    }, [data, visible, defaultCurrency]);

    const handleChange = (field, value) => {
        setFormData((prev) => ({ ...prev, [field]: value }));
        if (field === 'contractStartDate' || field === 'contractEndDate') setDateError(false);
    };

    const handleModelChange = (models) => {
        const modelIds = models.map((model) => model.id);
        setFormData((prev) => ({
            ...prev,
            selectedModels: models,
            modelIds: modelIds
        }));
    };

    const handleSubmit = () => {
        setSubmitted(true);

        if (!formData.modelIds || formData.modelIds.length === 0 || formData.warrHourlyRate === null || formData.normalHourlyRate === null || !formData.currencyUnits || !formData.contractStartDate) {
            return;
        }

        if (formData.contractEndDate && formData.contractStartDate && formData.contractEndDate < formData.contractStartDate) {
            setDateError(true);
            return;
        }

        const payload = {
            id: formData.id,
            siteUseId: formData.siteUseId,
            modelIds: formData.modelIds,
            warrHourlyRate: Number(formData.warrHourlyRate) || 0,
            normalHourlyRate: Number(formData.normalHourlyRate) || 0,
            currencyUnits: formData.currencyUnits,
            contractStartDate: formData.contractStartDate ? new Date(formData.contractStartDate).toISOString() : null,
            contractEndDate: formData.contractEndDate ? new Date(formData.contractEndDate).toISOString() : null
        };

        onSave(payload);
        onHide();
    };

    const footer = (
        <div className="flex justify-content-end gap-2">
            <Button label={th('common.cancel')} icon="pi pi-times" outlined onClick={onHide} />
            <Button label={th('common.save')} icon="pi pi-check" onClick={handleSubmit} />
        </div>
    );

    return (
        <Dialog
            header={data ? th('customerAddressBrand.tabs.payment.dialog.title.update') : th('customerAddressBrand.tabs.payment.dialog.title.new')}
            visible={visible}
            style={{ width: '55vw' }}
            modal
            className="p-fluid"
            onHide={onHide}
            footer={footer}
        >
            <div className="formgrid grid">
                <div className="field col-12 md:col-12">
                    <label htmlFor="models" className="font-bold block mb-2">
                        {th('customerAddressBrand.model')}
                    </label>
                    <PaymentModelMultiSelect
                        value={formData.selectedModels}
                        onChange={handleModelChange}
                        brand={brand}
                        placeholder={th('customerAddressBrand.placeholder.model')}
                        className={classNames({ 'p-invalid': submitted && (!formData.modelIds || formData.modelIds.length === 0) })}
                    />
                    {submitted && (!formData.modelIds || formData.modelIds.length === 0) && <small className="p-error">{th('common.required')}</small>}
                </div>

                <div className="field col-12 md:col-6">
                    <label htmlFor="warrHourlyRate" className="font-bold block mb-2">
                        {th('customerAddressBrand.tabs.payment.dialog.label.warrHourlyRate')}
                    </label>
                    <InputNumber
                        id="warrHourlyRate"
                        value={formData.warrHourlyRate}
                        onValueChange={(e) => handleChange('warrHourlyRate', e.value)}
                        mode="decimal"
                        minFractionDigits={2}
                        maxFractionDigits={4}
                        placeholder={th('customerAddressBrand.tabs.payment.dialog.placeholder.warrHourlyRate')}
                        className={classNames({ 'p-invalid': submitted && formData.warrHourlyRate === null })}
                        inputClassName="w-full"
                    />
                    {submitted && formData.warrHourlyRate === null && <small className="p-error">{th('common.required')}</small>}
                </div>

                <div className="field col-12 md:col-6">
                    <label htmlFor="normalHourlyRate" className="font-bold block mb-2">
                        {th('customerAddressBrand.tabs.payment.dialog.label.normalHourlyRate')}
                    </label>
                    <InputNumber
                        id="normalHourlyRate"
                        value={formData.normalHourlyRate}
                        onValueChange={(e) => handleChange('normalHourlyRate', e.value)}
                        mode="decimal"
                        minFractionDigits={2}
                        maxFractionDigits={4}
                        placeholder={th('customerAddressBrand.tabs.payment.dialog.placeholder.normalHourlyRate')}
                        className={classNames({ 'p-invalid': submitted && formData.normalHourlyRate === null })}
                        inputClassName="w-full"
                    />
                    {submitted && formData.normalHourlyRate === null && <small className="p-error">{th('common.required')}</small>}
                </div>

                <div className="field col-12 md:col-6">
                    <label htmlFor="currencyUnits" className="font-bold block mb-2">
                        {th('customerAddressBrand.tabs.payment.dialog.label.currencyUnits')}
                    </label>
                    <Dropdown
                        id="currencyUnits"
                        value={formData.currencyUnits}
                        options={currencyOptions}
                        onChange={(e) => handleChange('currencyUnits', e.value)}
                        placeholder={th('customerAddressBrand.tabs.payment.dialog.placeholder.currencyUnits')}
                        className={classNames({ 'p-invalid': submitted && !formData.currencyUnits })}
                        inputClassName="w-full"
                    />
                    {submitted && !formData.currencyUnits && <small className="p-error">{th('common.required')}</small>}
                </div>

                <div className="col-12 md:col-6"></div>

                <div className="field col-12 md:col-6">
                    <label htmlFor="contractStartDate" className="font-bold block mb-2">
                        {th('customerAddressBrand.tabs.payment.dialog.label.contractStartDate')}
                    </label>
                    <Calendar
                        id="contractStartDate"
                        value={formData.contractStartDate}
                        onChange={(e) => handleChange('contractStartDate', e.value)}
                        dateFormat="dd.mm.yy"
                        showIcon
                        placeholder={th('customerAddressBrand.tabs.payment.dialog.placeholder.contractStartDate')}
                        className={classNames({ 'p-invalid': submitted && !formData.contractStartDate })}
                        inputClassName="w-full"
                    />
                    {submitted && !formData.contractStartDate && <small className="p-error">{th('common.required')}</small>}
                </div>

                <div className="field col-12 md:col-6">
                    <label htmlFor="contractEndDate" className="font-bold block mb-2">
                        {th('customerAddressBrand.tabs.payment.dialog.label.contractEndDate')}
                    </label>
                    <Calendar
                        id="contractEndDate"
                        value={formData.contractEndDate}
                        onChange={(e) => handleChange('contractEndDate', e.value)}
                        dateFormat="dd.mm.yy"
                        showIcon
                        placeholder={th('customerAddressBrand.tabs.payment.dialog.placeholder.contractEndDate')}
                        minDate={formData.contractStartDate || null}
                        className={classNames({ 'p-invalid': dateError })}
                        inputClassName="w-full"
                    />
                    {dateError && <small className="p-error">{th('customerAddressBrand.tabs.payment.dialog.error.endDateBeforeStartDate')}</small>}
                </div>
            </div>
        </Dialog>
    );
};

export default PaymentDialog;
